import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import RangeSlide from "./RangeSlide.tsx";
import CommonClass from "@/utils/common";
import useCalc from "@/hooks/calculator.ts";
import CalculatorLimit from "@/types_and_interfaces/calculator_limit.ts";
import {CircularProgress} from "@mui/material";
import ProductInterface from "@/types_and_interfaces/product_interface.ts";
import InfoIcon from "@/svg/InfoIcon.tsx";
import CMS from "@/utils/cms.ts";
import {LocalLanguage} from "@/types_and_interfaces/types.ts";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import "katex/dist/katex.min.css";
import katex from "katex";

interface Props {
    styles:any
}

const CalculatorCore:React.FC<Props> = ({styles}) => {
    
    const { i18n} = useTranslation()
    const [money, setMoney] = useState<number>(0)
    const [period, setPeriod] = useState<number>(0)
    const first_debounce = useRef<boolean>(true)
    const {t} = useTranslation()
    const calc = useCalc()
    const product = calc.get_product() as ProductInterface
    const selected = calc.get_selected_type() as CalculatorLimit
    const inf_data = CMS.langAwareFilter(CMS.loan_info_content, (i18n.language as LocalLanguage))
    
    function cleanLatexContent(content: string): string {
        return content
            .replace(/<img[^>]*>/gi, "")
            .replace(/\/\/latex\/\/(.*?)\/\/latex\/\//gs, (_match, latexContent) => {
                const cleanedLatex = latexContent.replace(/<\/?[^>]+(>|$)/g, "").trim();
                
                return katex.renderToString(cleanedLatex, {
                    throwOnError: false,
                    displayMode: true,
                });
            });
    }
    
    const get_dynamic_content = (): { title: string; content: string } | {} => {
        if (typeof product === "object" && Object.keys(product).length) {
            let res: string = inf_data.content
                .replace("{{period}}", String(product?.months ?? ""))
                .replace("{{interest_rate}}", String(product?.interest_rate ?? ""))
                .replace("{{comission_rate}}", String(product?.commission_rate ?? ""))
                .replace("{{capital}}", String(product?.capital ?? ""))
                .replace("{{dae}}", String(product?.dae ?? ""))
                .replace("{{payable_amount}}", String(product?.payable_amount ?? ""))
                .replace("{{total_interest}}", String((product?.payable_amount ?? 0) - (product?.capital ?? 0)))
                .replace("{{penality}}", String(product?.penalty ?? ""))
                .replace("{{installment_amount}}", String(product?.installment_amount ?? ""));
            
            res = cleanLatexContent(res);
            
            return {
                title: inf_data.title,
                content: res,
            };
        } else {
            return {};
        }
    };
    
    const debounce_function = () => {
        if(!first_debounce.current) {
            calc.update_product(period, money)
        } else {
            first_debounce.current = true
        }
    }
    
    const before_debounce = () => {
        first_debounce.current = false
        calc.product_handler(undefined)
    }
    
    useEffect(() => {
        if(selected){
            
            setPeriod(selected?.default_month)
            setMoney(selected?.default_capital)
        }
    }, [selected]);
    
    
    return (
        <div className={styles.core}>
            
            <div className={clsx(styles.sides, styles.right, (typeof product === "object" && !Object.keys(product).length) && styles.error)}>
                
                {typeof product !== "undefined" ? (
                    <>
                        {Object.keys(product).length ? (
                            <>
                                {selected.category_name === "refinance" ? (
                                    <span>
                                        {t("pick_refinancing_period_and_amount")}
                                    </span>
                                ) : (
                                    <>
                                        <span>
                                            {t("monthly_payment")}
                                        </span>
                                        
                                        <div>
                                            <span className={styles.money}>
                                                {CommonClass.formatMoneyAmount((product as ProductInterface).installment_amount)?.replace(",00", "")} {t("currency")}
                                            </span>
                                            
                                            <button
                                                aria-label={"loan-info"}
                                                type={"button"}
                                                onClick={() => {
                                                    if (Object.keys(get_dynamic_content()).length) {
                                                        let obj = get_dynamic_content()
                                                        eventEmitter.emit(CommonClass.ModalEvents.SetCRInfo, obj)
                                                    }
                                                }}
                                            >
                                                <InfoIcon/>
                                            </button>
                                        </div>
                                    </>
                                )}
                            
                            
                            </>
                        ) : (
                            <span>
                                {t("calculator_product_not_found")}
                            </span>
                        )}
                    </>
                ) : (
                    <div className={styles.progress}>
                        <CircularProgress size={20} color={"inherit"}/>
                    </div>
                )}
            
            </div>
            
            <div className={clsx(styles.sides, styles.left)}>
                
                <RangeSlide
                    name={"amount"}
                    value={money}
                    label={t("amount")}
                    from={selected?.min_amount}
                    to={selected?.max_amount}
                    onChange={(n: number) => {
                        setMoney(n)
                    }}
                    money
                    segments_qt={1}
                    unity={t("currency")}
                    unityInSegments={true}
                    step_length={selected?.increment_value}
                    debounced={debounce_function}
                    before_debounce={before_debounce}
                />
                
                <RangeSlide
                    name={"period"}
                    value={period}
                    label={t("period")}
                    from={selected?.min_installments}
                    to={selected?.max_installments}
                    onChange={(n: number) => {
                        setPeriod(n)
                    }}
                    segments_qt={1}
                    unity={t(CommonClass.time_word_handling(period, true))}
                    unityInSegments={false}
                    debounced={debounce_function}
                    before_debounce={before_debounce}
                
                />
            
            </div>
        
        </div>
    )
}

export default CalculatorCore